<template>
  <v-card flat id="due-diligence-tab">
    <v-card-text>
      <v-alert
      v-if="isPending"
      type="info"
      text
      outlined
      dense
      icon="mdi-clock-outline"
      class="mb-4 dd-banner">
        <div class="font-weight-medium">{{ $t('awaitingSustainabilityApproval') }}</div>
        <div class="text-caption">{{ $t('awaitingSustainabilityApprovalBody', { user: requestedByName, date: submittedDate }) }}</div>
      </v-alert>

      <v-alert
      v-else-if="isApproved"
      type="success"
      text
      outlined
      dense
      icon="mdi-check-decagram"
      class="mb-4 dd-banner">
        <div class="font-weight-medium">{{ $t('dueDiligenceApproved') }}</div>
        <div class="text-caption">{{ $t('dueDiligenceApprovedBody', { user: resolvedByName, date: resolvedDate }) }}</div>
        <div v-if="latestApproval.comment" class="text-caption mt-1">
          <span class="font-weight-medium">{{ $t('feedback') }}:</span> {{ latestApproval.comment }}
        </div>
      </v-alert>

      <v-alert
      v-else-if="isReturned"
      type="error"
      text
      outlined
      dense
      icon="mdi-alert-circle-outline"
      class="mb-4 dd-banner">
        <div class="font-weight-medium">{{ $t('submissionRejectedHeader', { user: resolvedByName, date: resolvedDate }) }}</div>
        <div v-if="latestApproval.comment" class="text-caption mt-1">
          <span class="font-weight-medium">{{ $t('feedback') }}:</span> {{ latestApproval.comment }}
        </div>
      </v-alert>

      <div v-if="loading" class="text-center py-8">
        <v-progress-circular indeterminate color="primary"/>
      </div>

      <template v-else>
        <TractExemptionPanel
        class="mb-6"
        :exemption="exemption"
        :latestApproval="latestApproval"
        :readOnly="isReadOnly"
        :saving="savingExemption"
        @save="saveExemption"/>

        <v-sheet
        v-if="isMissingGeojson"
        rounded
        outlined
        class="pa-8 text-center">
          <v-icon size="40" color="grey">mdi-vector-polygon</v-icon>
          <p class="text-subtitle-1 mt-3 mb-1">{{ $t('noGeojsonTitle') }}</p>
          <p class="text-body-2 grey--text text--darken-1 mx-auto mb-4" style="max-width: 28vw;">{{ $t('noGeojsonBody') }}</p>
        </v-sheet>

        <template v-else>
          <div class="d-flex align-center mb-1">
            <v-icon color="primary" class="mr-2">mdi-clipboard-search-outline</v-icon>
            <span class="text-h6 font-weight-regular">{{ $t('groupHits') }}</span>
            <v-chip v-if="items.length > 0" small class="ml-3 dd-count-pill" label>{{ $t('xOfYAddressed', { addressed: addressedCount, total: items.length }) }}</v-chip>
            <v-spacer/>
            <v-tooltip v-if="manualModeEnabled() && !isReadOnly && items.length > 0" bottom color="grey" :disabled="availableGroups.length > 0">
              <template #activator="{ on }">
                <span v-on="on" class="mr-2">
                  <v-btn
                  outlined
                  color="primary"
                  :disabled="availableGroups.length === 0"
                  @click="openAdd">
                    <v-icon left>mdi-plus</v-icon>
                    {{ $t('addGroupHit') }}
                  </v-btn>
                </span>
              </template>
              <span class="white--text text-subtitle-2">{{ $t('allGroupsHaveItems') }}</span>
            </v-tooltip>
            <Icon
            v-if="canSubmit && resubmitUnchanged"
            icon="mdi-alert"
            iconColor="warning"
            :small="false"
            margin="mr-2"
            :tooltipText="$t('resubmitUnchangedWarning')"/>
            <v-tooltip v-if="canSubmit" bottom color="grey" :disabled="submitBlockedReason === null">
              <template #activator="{ on }">
                <span v-on="on">
                  <v-btn
                  color="primary"
                  :disabled="submitBlockedReason !== null"
                  @click="openSubmit">
                    <v-icon left>mdi-send-check-outline</v-icon>
                    {{ $t('submitDueDiligence') }}
                  </v-btn>
                </span>
              </template>
              <span class="white--text text-subtitle-2">{{ submitBlockedTooltip }}</span>
            </v-tooltip>
          </div>
          <p class="text-caption grey--text text--darken-1" :class="showExemptItemsNote ? 'mb-1' : 'mb-4'">{{ groupHitsSubtitleText }}</p>
          <!-- An exemption suspends the completeness requirement; the entries themselves stay on record -->
          <p v-if="showExemptItemsNote" class="text-caption grey--text text--darken-1 mb-4">
            <v-icon x-small class="mr-1">mdi-information-outline</v-icon>{{ $t('exemptItemsNotRequiredInfo') }}
          </p>

          <v-sheet
          v-if="isEmptyManual"
          outlined
          rounded
          class="pa-8 text-center">
            <v-icon size="40" color="grey">mdi-clipboard-outline</v-icon>
            <p class="text-subtitle-1 mt-3 mb-1">{{ $t('noGroupHitsYetTitle') }}</p>
            <p class="text-body-2 grey--text text--darken-1 mx-auto mb-4" style="max-width: 520px;">{{ $t('noGroupHitsYetBody') }}</p>
            <v-btn v-if="!isReadOnly" color="primary" @click="openCapture">
              <v-icon left>mdi-plus</v-icon>
              {{ $t('addSpeciesHits') }}
            </v-btn>
          </v-sheet>

          <v-sheet
          v-else-if="isNoHits"
          outlined
          rounded
          class="pa-8 text-center">
            <v-icon size="40" color="grey">mdi-map-search-outline</v-icon>
            <p class="text-subtitle-1 mt-3 mb-1">{{ $t('noGroupHitsDetectedTitle') }}</p>
            <p class="text-body-2 grey--text text--darken-1 mx-auto mb-0" style="max-width: 28vw;">{{ $t('noGroupHitsDetectedBody') }}</p>
            <p v-if="!hasExemption" class="text-body-2 grey--text text--darken-1 mx-auto mb-0 mt-2" style="max-width: 28vw;">{{ $t('noGroupHitsDetectedSelectExemption') }}</p>
            <v-btn v-if="!isReadOnly" color="primary" class="mt-4" :loading="recheckingSpecies" @click="recheckSpeciesLayer">
              <v-icon left>mdi-refresh</v-icon>
              {{ $t('recheckSpeciesLayer') }}
            </v-btn>
          </v-sheet>

          <div v-else class="d-flex flex-column" style="gap: 14px;">
            <v-card
            v-for="item in sortedItems"
            :key="item.tractMitigationItemId"
            outlined
            class="dd-stripe-card">
              <div class="dd-stripe" :style="{ background: groupColor(item) }"></div>
              <div class="ds-card-inner">
                <div class="d-flex align-center px-4 py-3">
                  <v-icon :color="groupColor(item)" class="mr-2">mdi-paw</v-icon>
                  <span class="text-subtitle-1">{{ groupName(item) }}</span>
                  <v-chip v-if="speciesCount(item) > 0" x-small label class="ml-3 grey lighten-3">{{ speciesCountLabel(item) }}</v-chip>
                  <v-spacer/>
                  <span v-if="isAddressed(item)" class="success--text text-caption d-inline-flex align-center">
                    <v-icon small color="success" class="mr-1">mdi-check-circle</v-icon>
                    {{ $t('mitigationRecorded') }}
                  </span>
                </div>

                <v-divider/>

                <div v-if="isAddressed(item)" class="px-4 py-2 grey lighten-5">
                  <div class="d-flex align-center justify-space-between flex-wrap">
                    <div class="d-flex align-center flex-wrap" style="gap: 8px;">
                      <span class="dd-eyebrow">{{ presenceLabel(item) }}</span>
                      <PresenceChip :present="isPresent(item)"/>
                    </div>
                    <div v-if="!isReadOnly">
                      <v-btn icon small color="success" @click="openRecord(item)"><v-icon small>mdi-pencil</v-icon></v-btn>
                      <v-btn icon small color="error" @click="promptDelete(item)"><v-icon small>mdi-delete</v-icon></v-btn>
                    </div>
                    <v-btn v-else small text color="primary" @click="openView(item)">
                      <v-icon small left>mdi-eye-outline</v-icon>{{ $t('view') }}
                    </v-btn>
                  </div>

                  <HabitatAutocomplete
                  v-if="showHabitatRow(item)"
                  :value="item.mitigationHabitatIds || []"
                  :habitats="allMitigationHabitats"
                  readOnly
                  class="mt-3"/>

                  <MitigationCommentPreview v-if="item.mitigationComment" :comment="item.mitigationComment" class="mt-3"/>
                </div>

                <div v-else class="px-4 py-3 grey lighten-5 d-flex align-center justify-space-between">
                  <span class="text-caption font-italic grey--text text--darken-1">{{ $t('noMitigationYet') }}</span>
                  <v-btn v-if="!isReadOnly" small color="primary" @click="openRecord(item)">
                    <v-icon small left>mdi-plus</v-icon>{{ $t('recordMitigation') }}
                  </v-btn>
                  <v-btn v-else small text color="primary" @click="openView(item)">
                    <v-icon small left>mdi-eye-outline</v-icon>{{ $t('view') }}
                  </v-btn>
                </div>
              </div>
            </v-card>
          </div>
        </template>
      </template>
    </v-card-text>

    <Dialog
    :stateId="dialogId"
    :maxWidth="dialogMaxWidth"
    :contentClass="(dialogMode === 'record' && natureServeOpen) ? 'rmd-dialog-shifted' : undefined"
    @click:outside="closeDialog"
    @dialog-closing="closeDialog"
    @close="closeDialog">
      <RecordMitigationDialog
      v-if="dialogMode === 'record' && activeItem"
      :item="activeItem"
      :group="activeGroup"
      :availableGroups="availableGroups"
      :speciesEditable="manualModeEnabled()"
      :habitats="allMitigationHabitats"
      :readOnly="dialogReadOnly"
      :natureServeResult="natureServeResult"
      :tractId="tract.tractId"
      :latitude="tract.spot?.latitude"
      :longitude="tract.spot?.longitude"
      @save="saveMitigation"
      @cancel="closeDialog"
      @check-nature-serve="openNatureServe"/>
      <AddSpeciesHitsDialog
      v-else-if="dialogMode === 'capture'"
      :key="captureKey"
      :groups="allMitigationGroups"
      :initialRows="captureRows"
      :natureServeResult="natureServeResult"
      @save="saveCapture"
      @cancel="closeDialog"
      @check-nature-serve="openNatureServe"/>
      <ConfirmDialog
      v-else-if="dialogMode === 'delete'"
      :title="$t('deleteMitigationTitle')"
      :body="$t('deleteMitigationBody')"
      color="error"
      @confirm="performDelete"
      @cancel="closeDialog"/>
      <ConfirmDialog
      v-else-if="dialogMode === 'submit'"
      :title="$t('confirmSubmitDueDiligenceTitle')"
      :body="submitDialogBody"
      color="primary"
      :confirmText="$t('submitDueDiligence')"
      @confirm="performSubmit"
      @cancel="closeDialog">
        <template #custom-body>
          <v-alert
          v-if="resubmitUnchanged"
          type="warning"
          text
          outlined
          dense
          icon="mdi-alert-outline"
          class="mt-4 mb-0 dd-resubmit-note">
            <span class="text-caption">{{ $t('confirmResubmitUnchangedNote') }}</span>
          </v-alert>
        </template>
      </ConfirmDialog>
    </Dialog>

    <NatureServeSlideOver
    v-model="natureServeOpen"
    :species="natureServeSpecies"
    :stateCode="tract.spot?.countrySubdivision"
    @resolved="natureServeResult = $event"/>
  </v-card>
</template>

<script>
import { mapActions, mapGetters } from 'vuex'
import { DueDiligenceApprovalStatus, RiskPresence, UserClaims } from '@/utils/Enumerations.js'
import { utcToLocalDate } from '@/utils/DateFormatter.js'
import { uniqueDialogId } from '@/utils/componentHelpers'
import { DialogWidth } from '@/utils/Dialog.js'
import { angelGeojsonClient, AngelGeojson, AngelGeojsonLayer } from '@/utils/map/Geojson'
import { presenceLabel, speciesCountLabel } from '@/utils/dueDiligence/presentation.js'
import { compareGroupRowPrecedence } from '@/utils/dueDiligence/groupPrecedence.js'
import { dueDiligenceApprovalEnabled, manualModeEnabled } from '@/utils/dueDiligence/mode.js'
import { seedMitigationItems } from '@/utils/dueDiligence/seedMitigationItems.js'

export default {
  name: 'DueDiligence',

  components: {
    Dialog: () => import('@/components/Dialog.vue'),
    RecordMitigationDialog: () => import('@/components/tract/tract-form/due-diligence/RecordMitigationDialog.vue'),
    AddSpeciesHitsDialog: () => import('@/components/tract/tract-form/due-diligence/AddSpeciesHitsDialog.vue'),
    NatureServeSlideOver: () => import('@/components/tract/tract-form/due-diligence/NatureServeSlideOver.vue'),
    ConfirmDialog: () => import('@/components/helper/ConfirmDialog.vue'),
    Icon: () => import('@/components/helper/Icon.vue'),
    MitigationCommentPreview: () => import('@/components/due-diligence/MitigationCommentPreview.vue'),
    HabitatAutocomplete: () => import('@/components/due-diligence/HabitatAutocomplete.vue'),
    PresenceChip: () => import('@/components/due-diligence/PresenceChip.vue'),
    TractExemptionPanel: () => import('@/components/due-diligence/TractExemptionPanel.vue')
  },

  props: {
    tract: {
      type: Object,
      required: true
    },
    refreshDueDiligence: {
      type: Boolean,
      default: false
    }
  },

  data: () => ({
    items: [],
    hasGeojson: true,
    loading: false,
    dialogMode: null, // 'record' | 'capture' | 'delete' | 'submit'
    dialogReadOnly: false,
    dialogId: uniqueDialogId('due-diligence-dialog'),
    activeItem: null,
    pendingDeleteItem: null,
    captureRows: [],
    captureKey: 0,
    natureServeOpen: false,
    natureServeSpecies: null,
    natureServeResult: null,
    rejectedSnapshot: null,
    savingExemption: false,
    recheckingSpecies: false,
    hasActiveReviewer: true
  }),

  created () {
    this.fetchGroupsAndHabitats()
    this.loadItems()
    // A refresh requested before this tab first rendered is satisfied by the initial load
    if (this.refreshDueDiligence) this.$emit('due-diligence-refreshed')
    // Only feeds the submit tooltip, which is gone when the approval workflow is off
    if (!this.isReadOnly && dueDiligenceApprovalEnabled()) this.loadReviewerAvailability()
  },

  watch: {
    refreshDueDiligence (shouldRefresh) {
      if (shouldRefresh) this.refreshItems()
    },

    rejectionSnapshotKey () {
      this.ensureRejectionSnapshot()
    },

    natureServeOpen (open) {
      if (this.dialogMode !== 'record') return
      this.openOrUpdateDialog({ id: this.dialogId, width: open ? '72vw' : '85vw' })
    }
  },

  computed: {
    ...mapGetters('mitigation-group', ['allMitigationGroups']),
    ...mapGetters('mitigation-habitat', ['allMitigationHabitats']),

    exemption () {
      return this.tract.dueDiligenceExemption ?? null
    },

    hasExemption () {
      return this.exemption !== null
    },

    isNoHits () {
      return !manualModeEnabled() && this.items.length === 0 && this.hasGeojson
    },

    showExemptItemsNote () {
      return this.hasExemption && this.items.length > 0
    },

    isMissingGeojson () {
      return !manualModeEnabled() && this.items.length === 0 && !this.hasGeojson
    },

    isEmptyManual () {
      return manualModeEnabled() && this.items.length === 0
    },

    groupsById () {
      return new Map(this.allMitigationGroups.map(g => [g.mitigationGroupId, g]))
    },

    availableGroups () {
      return this.allMitigationGroups.filter(g => !this.items.some(i => i.mitigationGroupId === g.mitigationGroupId))
    },

    sortedItems () {
      return [...this.items].sort(compareGroupRowPrecedence)
    },

    groupHitsSubtitleText () {
      return manualModeEnabled() ? this.$t('groupHitsSubtitleManual') : this.$t('groupHitsSubtitle')
    },

    latestApproval () {
      return this.tract.latestDueDiligenceApproval ?? null
    },

    isPending () {
      return this.latestApproval?.status === DueDiligenceApprovalStatus.PendingReview.value
    },

    isApproved () {
      return this.latestApproval?.status === DueDiligenceApprovalStatus.Approved.value
    },

    isReturned () {
      return this.latestApproval?.status === DueDiligenceApprovalStatus.Returned.value
    },

    isReadOnly () {
      return this.isPending || this.isApproved
    },

    canSubmit () {
      if (!dueDiligenceApprovalEnabled()) return false
      if (this.isPending || this.isApproved) return false
      return this.hasExemption ? !!this.exemption.requiresApproval : this.items.length > 0
    },

    submitBlockedReason () {
      if (!this.hasActiveReviewer) return 'noReviewer'
      if (!this.submitReady) return 'incomplete'
      return null
    },

    submitBlockedTooltip () {
      switch (this.submitBlockedReason) {
        case 'noReviewer': return this.$t('submitRequiresSustainabilityAdmin')
        case 'incomplete': return this.$t('submitRequiresAllAddressed')
        default: return ''
      }
    },

    // Advisory only — a rejection can be wrong, so an unchanged resubmission stays allowed
    resubmitUnchanged () {
      if (!this.isReturned || this.rejectedSnapshot == null) return false
      return this.submissionFingerprint() === this.rejectedSnapshot
    },

    // Keyed by the Returned row so the snapshot survives reloads and goes stale with the cycle.
    rejectionSnapshotKey () {
      const approvalId = this.latestApproval?.tractDueDiligenceApprovalId
      return this.isReturned && approvalId != null
        ? `DUE_DILIGENCE_REJECTED_SNAPSHOT_${this.tract.tractId}_${approvalId}`
        : null
    },

    addressedCount () {
      return this.items.filter(this.isAddressed).length
    },

    allAddressed () {
      return this.items.length > 0 && this.addressedCount === this.items.length
    },

    submitReady () {
      return this.hasExemption || this.allAddressed
    },

    submitDialogBody () {
      return this.hasExemption
        ? this.$t('confirmSubmitExemptionBody', { name: this.tract.name, exemption: this.exemption.name })
        : this.$t('confirmSubmitDueDiligenceBody', { name: this.tract.name })
    },

    activeGroup () {
      return this.groupFor(this.activeItem)
    },

    isConfirmMode () {
      return this.dialogMode === 'delete' || this.dialogMode === 'submit'
    },

    dialogMaxWidth () {
      if (this.dialogMode === 'record') return '85vw'
      if (this.dialogMode === 'capture') return '50vw'
      return DialogWidth.Simple
    },

    requestedByName () {
      return this.latestApproval?.requestedByUserName ?? ''
    },

    resolvedByName () {
      return this.latestApproval?.resolvedByUserName ?? ''
    },

    submittedDate () {
      return this.latestApproval?.requestedAt ? utcToLocalDate(this.latestApproval.requestedAt) : ''
    },

    resolvedDate () {
      return this.latestApproval?.resolvedAt ? utcToLocalDate(this.latestApproval.resolvedAt) : ''
    }
  },

  methods: {
    ...mapActions('user', ['fetchActiveUsersWithClaim']),
    ...mapActions('mitigation-group', ['fetchMitigationGroups']),
    ...mapActions('mitigation-habitat', ['fetchMitigationHabitats']),
    ...mapActions('dialog', ['openOrUpdateDialog', 'closeDialogsAtOrAbove']),
    ...mapActions('tract', [
      'fetchTractMitigationItems',
      'createTractMitigationItem',
      'updateTractMitigationItem',
      'deleteTractMitigationItem',
      'submitDueDiligence',
      'setTractDueDiligenceExemption'
    ]),

    manualModeEnabled,

    async loadReviewerAvailability () {
      try {
        const reviewers = await this.fetchActiveUsersWithClaim(UserClaims.SustainabilityAdmin)
        this.hasActiveReviewer = reviewers.length > 0
      } catch {
        this.hasActiveReviewer = true
      }
    },

    async fetchGroupsAndHabitats () {
      await Promise.all([
        this.allMitigationGroups.length === 0 ? this.fetchMitigationGroups() : null,
        this.allMitigationHabitats.length === 0 ? this.fetchMitigationHabitats() : null
      ])
    },

    async saveExemption ({ dueDiligenceExemptionId }) {
      this.savingExemption = true
      try {
        await this.setTractDueDiligenceExemption({ tractId: this.tract.tractId, dueDiligenceExemptionId, modifiedAt: this.tract.modifiedAt })
        this.setSnack(this.$t(dueDiligenceExemptionId == null ? 'exemptionCleared' : 'exemptionUpdated'))
        this.$emit('due-diligence-updated')
      } finally {
        this.savingExemption = false
      }
    },

    async refreshItems () {
      try {
        await this.loadItems()
      } finally {
        this.$emit('due-diligence-refreshed')
      }
    },

    async storedCutLayer () {
      const geojson = await angelGeojsonClient.readGeojson(this.tract.tractId)
      return geojson === null ? null : new AngelGeojson(geojson).getLayer(AngelGeojsonLayer.Cut)
    },

    async tractHasCutLayer () {
      const cut = await this.storedCutLayer()
      return cut?.features?.length > 0
    },

    async recheckSpeciesLayer () {
      this.recheckingSpecies = true
      try {
        const { seeded, itemCount, warnings } = await seedMitigationItems(this.tract.tractId, await this.storedCutLayer())
        if (seeded && itemCount === 0 && warnings.length === 0) this.setSnack(this.$t('speciesRecheckNoHits'))
        await this.loadItems()
        if (seeded) this.$emit('due-diligence-updated')
      } finally {
        this.recheckingSpecies = false
      }
    },

    async loadItems () {
      this.loading = true
      try {
        const items = await this.fetchTractMitigationItems(this.tract.tractId)
        this.items = items
        this.hasGeojson = items.length > 0 || await this.tractHasCutLayer()
        this.ensureRejectionSnapshot()
      } finally {
        this.loading = false
      }
    },

    // Order-independent form of what a submission carries — the exemption plus each entry —
    // the basis for "has anything changed since the rejection". The exemption belongs here:
    // on a no-hits tract it is the entire substance of the submission.
    submissionFingerprint () {
      return JSON.stringify({
        exemption: this.exemption?.dueDiligenceExemptionId ?? null,
        items: (this.items ?? [])
          .map(item => ({
            group: item.mitigationGroupId,
            presence: item.riskPresence ?? RiskPresence.NotSet.value,
            habitats: [...(item.mitigationHabitatIds ?? [])].sort((a, b) => a - b),
            comment: (item.mitigationComment ?? '').trim(),
            species: (item.speciesHits ?? [])
              .map(sp => [(sp.commonName ?? '').trim(), (sp.scientificName ?? '').trim()])
              .sort((a, b) => a[0].localeCompare(b[0]) || a[1].localeCompare(b[1]))
          }))
          .sort((a, b) => a.group - b.group)
      })
    },

    ensureRejectionSnapshot () {
      if (this.rejectionSnapshotKey == null) {
        this.rejectedSnapshot = null
        return
      }
      let stored = localStorage.getItem(this.rejectionSnapshotKey)
      if (!stored) {
        stored = this.submissionFingerprint()
        localStorage.setItem(this.rejectionSnapshotKey, stored)
      }
      this.rejectedSnapshot = stored
    },

    groupFor (item) {
      if (!item) return null
      return this.groupsById.get(item.mitigationGroupId) ?? null
    },

    groupName (item) {
      return this.groupFor(item)?.name ?? ''
    },

    groupColor (item) {
      return this.groupFor(item)?.color ?? 'primary'
    },

    requiresHabitat (item) {
      return !!this.groupFor(item)?.requiresHabitat
    },

    presenceLabel (item) {
      return presenceLabel(this.requiresHabitat(item))
    },

    isAddressed (item) {
      return item.riskPresence != null && item.riskPresence !== RiskPresence.NotSet.value
    },

    isPresent (item) {
      return item.riskPresence === RiskPresence.Present.value
    },

    speciesCount (item) {
      return item.speciesHits?.length ?? 0
    },

    speciesCountLabel (item) {
      return speciesCountLabel(this.speciesCount(item))
    },

    showHabitatRow (item) {
      return this.requiresHabitat(item) || (item.mitigationHabitatIds?.length > 0)
    },

    openAdd () {
      this.activeItem = {
        tractMitigationItemId: null,
        mitigationGroupId: null,
        riskPresence: RiskPresence.NotSet.value,
        mitigationHabitatIds: [],
        mitigationComment: '',
        speciesHits: []
      }
      this.dialogReadOnly = false
      this.openMode('record')
    },

    // Manual mode with no items yet. Once items exist, further group hits go through openAdd
    openCapture () {
      this.captureRows = []
      this.captureKey++
      this.openMode('capture')
    },

    openRecord (item) {
      this.activeItem = item
      this.dialogReadOnly = false
      this.openMode('record')
    },

    openView (item) {
      this.activeItem = item
      this.dialogReadOnly = true
      this.openMode('record')
    },

    promptDelete (item) {
      this.pendingDeleteItem = item
      this.openMode('delete')
    },

    openSubmit () {
      this.openMode('submit')
    },

    openMode (mode) {
      this.dialogMode = mode
      this.openOrUpdateDialog({
        id: this.dialogId,
        width: mode === 'record' ? '85vw' : '',
        allowFullscreen: !this.isConfirmMode
      })
    },

    closeDialog () {
      this.closeDialogsAtOrAbove(this.dialogId)
      this.dialogMode = null
      this.dialogReadOnly = false
      this.activeItem = null
      this.pendingDeleteItem = null
      this.captureRows = []
      this.natureServeOpen = false
      this.natureServeResult = null
    },

    async saveMitigation (payload) {
      const requestObj = { tractId: this.tract.tractId, mitigationItem: payload }
      if (payload.tractMitigationItemId != null) {
        await this.updateTractMitigationItem(requestObj)
        this.setSnack(this.$t('mitigationUpdatedSuccess'))
      } else {
        await this.createTractMitigationItem(requestObj)
        this.setSnack(this.$t('mitigationRecordedSuccess'))
      }
      this.closeDialog()
      await this.loadItems()
    },

    async saveCapture (groupPayloads) {
      for (let i = 0; i < groupPayloads.length; i++) {
        const { mitigationGroupId, speciesHits } = groupPayloads[i]
        try {
          await this.createTractMitigationItem({
            tractId: this.tract.tractId,
            mitigationItem: {
              tractMitigationItemId: null,
              mitigationGroupId,
              riskPresence: RiskPresence.NotSet.value,
              mitigationHabitatIds: [],
              mitigationComment: '',
              speciesHits
            }
          })
        } catch (error) {
          // Interceptor already snacked the error — reload so the groups that saved
          // render as cards, and reopen with only the unsaved rows.
          this.captureRows = groupPayloads.slice(i).flatMap(payload => payload.speciesHits.length
            ? payload.speciesHits.map(sp => ({
              commonName: sp.commonName ?? '',
              scientificName: sp.scientificName ?? '',
              mitigationGroupId: payload.mitigationGroupId
            }))
            : [{ commonName: '', scientificName: '', mitigationGroupId: payload.mitigationGroupId }])
          this.captureKey++
          await this.loadItems()
          return
        }
      }
      this.setSnack(this.$t('speciesHitsRecordedSuccess'))
      this.closeDialog()
      await this.loadItems()
    },

    async performDelete () {
      await this.deleteTractMitigationItem({
        tractId: this.tract.tractId,
        tractMitigationItemId: this.pendingDeleteItem.tractMitigationItemId
      })
      this.setSnack(this.$t('mitigationDeletedSuccess'))
      this.closeDialog()
      await this.loadItems()
      this.$emit('due-diligence-updated')
    },

    async performSubmit () {
      await this.submitDueDiligence(this.tract.tractId)
      // The rejected cycle is superseded by the new pending one — drop its stale snapshot
      if (this.rejectionSnapshotKey != null) localStorage.removeItem(this.rejectionSnapshotKey)
      this.setSnack(this.$t('dueDiligenceSubmitted'))
      this.closeDialog()
      this.$emit('due-diligence-updated')
    },

    openNatureServe (species) {
      this.natureServeResult = null
      this.natureServeSpecies = species
      this.natureServeOpen = true
    }
  }
}
</script>

<style scoped>
.ds-card-inner {
  padding-left: 5px;
}

/* Vuetify top-aligns the alert icon; centering reads better against a single-line note */
.dd-resubmit-note ::v-deep .v-alert__icon {
  align-self: center;
}
</style>

<style>
.v-dialog.rmd-dialog-shifted:not(.v-dialog--fullscreen) {
  margin-right: calc(24vw + 12px);
}
</style>
